import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { api, type AIAnnotation, type AIContentType } from "../api";
import { useAuth } from "../auth";
import { Avatar } from "./Avatar";

interface Props {
  bookId: number;
  type: AIContentType;
}

// Inline flag/note thread for an AI section. Shows under the section's
// content when there are open annotations or when the composer is open.
// "Flag" = something here is wrong / should be corrected.
// "Note"  = supplementary thought worth attaching.
export function SectionAnnotations({ bookId, type }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [composerOpen, setComposerOpen] = useState(false);
  const [kind, setKind] = useState<"flag" | "note">("flag");
  const [body, setBody] = useState("");

  const list = useQuery({
    queryKey: ["annotations", bookId, type],
    queryFn: () =>
      api.get<{ annotations: AIAnnotation[] }>(`/annotations/${bookId}/${type}`),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post(`/annotations/${bookId}/${type}`, { kind, body: body.trim() }),
    onSuccess: () => {
      setBody("");
      setComposerOpen(false);
      qc.invalidateQueries({ queryKey: ["annotations", bookId, type] });
      qc.invalidateQueries({ queryKey: ["annotation-counts", bookId] });
    },
  });

  const resolve = useMutation({
    mutationFn: (id: number) => api.post(`/annotations/${id}/resolve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["annotations", bookId, type] });
      qc.invalidateQueries({ queryKey: ["annotation-counts", bookId] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/annotations/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["annotations", bookId, type] });
      qc.invalidateQueries({ queryKey: ["annotation-counts", bookId] });
    },
  });

  const open = list.data?.annotations ?? [];
  const flags = open.filter((a) => a.kind === "flag");
  const notes = open.filter((a) => a.kind === "note");

  return (
    <div className="mt-6 pt-5 border-t border-cream-200">
      <div className="flex items-center gap-2 flex-wrap">
        {flags.length > 0 ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-light/20 text-rose-dark text-xs font-medium">
            ⚐ {flags.length} {flags.length === 1 ? "correction" : "corrections"}
          </span>
        ) : null}
        {notes.length > 0 ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold/15 text-gold-dark text-xs font-medium">
            ✶ {notes.length} {notes.length === 1 ? "note" : "notes"}
          </span>
        ) : null}
        <button
          onClick={() => {
            setComposerOpen((v) => !v);
            if (!composerOpen) setKind("flag");
          }}
          className="ml-auto text-[0.7rem] uppercase tracking-[0.14em] text-plum-mute hover:text-rose px-2 py-1"
        >
          {composerOpen ? "Cancel" : "Flag or annotate"}
        </button>
      </div>

      {composerOpen ? (
        <div className="mt-3 p-4 rounded-xl bg-cream-50 border border-cream-300 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <KindPill label="Flag a correction" active={kind === "flag"} onClick={() => setKind("flag")} tone="rose" />
            <KindPill label="Add a note" active={kind === "note"} onClick={() => setKind("note")} tone="gold" />
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder={
              kind === "flag"
                ? "What's wrong here? e.g. “Chapter 7 isn't titled 'The Window' — that was chapter 9.”"
                : "What's worth knowing about this section?"
            }
            className="w-full px-3 py-2 rounded-lg bg-white border border-cream-300 text-sm text-plum placeholder:text-plum-mute placeholder:italic resize-none focus:outline-none focus:border-rose"
            autoFocus
          />
          <div className="flex justify-end">
            <button
              disabled={!body.trim() || create.isPending}
              onClick={() => create.mutate()}
              className="text-xs uppercase tracking-[0.14em] px-4 py-1.5 rounded-full bg-rose text-white hover:bg-rose-dark disabled:opacity-50"
            >
              {create.isPending ? "Saving…" : "Post"}
            </button>
          </div>
        </div>
      ) : null}

      {open.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-3">
          {open.map((a) => (
            <li key={a.id} className="flex gap-3">
              {a.author ? (
                <Avatar name={a.author.name} email={a.author.email} size="sm" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-cream-200 shrink-0" />
              )}
              <div
                className={[
                  "flex-1 min-w-0 px-4 py-3 rounded-xl border",
                  a.kind === "flag"
                    ? "bg-rose-light/10 border-rose-light/30"
                    : "bg-gold/8 border-gold/20",
                ].join(" ")}
              >
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-plum text-sm">
                      {a.author?.name ?? a.author?.email.split("@")[0] ?? "Someone"}
                    </span>
                    <span
                      className={[
                        "text-[0.6rem] uppercase tracking-[0.16em] font-medium",
                        a.kind === "flag" ? "text-rose-dark" : "text-gold-dark",
                      ].join(" ")}
                    >
                      {a.kind === "flag" ? "correction" : "note"}
                    </span>
                  </div>
                  <span className="text-[0.65rem] text-plum-mute">
                    {format(parseISO(a.createdAt.replace(" ", "T") + "Z"), "MMM d, h:mm a")}
                  </span>
                </div>
                <p className="text-plum-soft text-sm leading-relaxed mt-1.5 whitespace-pre-wrap">
                  {a.body}
                </p>
                <div className="mt-2 flex items-center gap-3 justify-end">
                  <button
                    onClick={() => resolve.mutate(a.id)}
                    className="text-[0.65rem] uppercase tracking-[0.14em] text-plum-mute hover:text-rose"
                  >
                    Resolve
                  </button>
                  {a.author?.id === user?.id ? (
                    <button
                      onClick={() => remove.mutate(a.id)}
                      className="text-[0.65rem] uppercase tracking-[0.14em] text-plum-mute hover:text-rose"
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function KindPill({
  label,
  active,
  onClick,
  tone,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  tone: "rose" | "gold";
}) {
  const activeClass = tone === "rose" ? "bg-rose text-white" : "bg-gold text-white";
  return (
    <button
      onClick={onClick}
      className={[
        "px-3 py-1 rounded-full text-xs uppercase tracking-[0.14em] transition-colors",
        active ? activeClass : "bg-cream-100 text-plum-soft hover:bg-cream-200",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
